import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Button,
  Snackbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import useOrgTemplates from './hooks/useOrgTemplates';
import useOrgFindingTypes from './hooks/useOrgFindingTypes';
import useOrgZones from './hooks/useOrgZones';
import useTemplateMutations from './hooks/useTemplateMutations';
import TemplateEditor from './TemplateEditor';
import { dialogPaperProps } from './darkModeStyles';

import '../../settingsStyles.css';

function getDisplayStatus(status: string): string {
  return status === 'published' ? 'Published' : 'Unpublished';
}

function getStatusColor(status: string): 'success' | 'warning' {
  return status === 'published' ? 'success' : 'warning';
}

function TaskListManagement() {
  const orgID = (global as any)?.me?.orgID;
  const isPaivcamSeed = orgID === 'paivcam-seed';

  const {
    templates,
    isLoading: templatesLoading,
    error: templatesError,
    refetch: refetchTemplates,
  } = useOrgTemplates();

  const {
    findingTypes,
    isLoading: findingTypesLoading,
    error: findingTypesError,
    refetch: refetchFindingTypes,
  } = useOrgFindingTypes();

  const {
    zones,
    isLoading: zonesLoading,
    error: zonesError,
    refetch: refetchZones,
  } = useOrgZones();

  const { cloneTemplate } = useTemplateMutations();

  const isLoading = templatesLoading || findingTypesLoading || zonesLoading;
  const error = templatesError || findingTypesError || zonesError;

  // ── Two-section split ──
  const systemTemplates = useMemo(
    () => templates.filter((t) => t.isSystem),
    [templates]
  );
  const orgTemplates = useMemo(
    () => templates.filter((t) => !t.isSystem),
    [templates]
  );

  // ── View state: when a template is selected, delegate to TemplateEditor ──
  const [viewingTemplateId, setViewingTemplateId] = useState<string | null>(
    null
  );

  // ── Clone state ──
  const [cloning, setCloning] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloneSystemTemplate = useCallback(
    async (templateId: string) => {
      setCloning(true);
      try {
        const result = await cloneTemplate(templateId);
        if (result?.success) {
          showSnackbar('Template cloned successfully', 'success');
          refetchTemplates();
          refetchFindingTypes();
          refetchZones();
        } else {
          showSnackbar(result?.error ?? 'Failed to clone template', 'error');
        }
      } catch (err: any) {
        showSnackbar(err?.message ?? 'Failed to clone template', 'error');
      } finally {
        setCloning(false);
      }
    },
    [cloneTemplate, refetchTemplates, refetchFindingTypes, refetchZones]
  );

  // Only show full-page loading spinner on initial load, not during background refetches
  if (isLoading && !viewingTemplateId && templates.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  // ── If viewing a specific template, delegate to TemplateEditor ──
  if (viewingTemplateId) {
    return (
      <Box>
        <Typography
          variant="h4"
          sx={(theme) => ({
            mb: 1,
            fontWeight: 'bold',
            color: theme.custom?.text,
          })}
        >
          Task List Management
        </Typography>
        <TemplateEditor
          templates={templates}
          findingTypes={findingTypes}
          zones={zones}
          onRefreshTemplates={refetchTemplates}
          onRefreshFindingTypes={refetchFindingTypes}
          onRefreshZones={refetchZones}
          isPaivcamSeed={isPaivcamSeed}
          initialTemplateId={viewingTemplateId}
          onBackToList={() => setViewingTemplateId(null)}
        />
      </Box>
    );
  }

  // ── List view: two-section layout ──
  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography
          variant="h4"
          sx={(theme) => ({
            fontWeight: 'bold',
            color: theme.custom?.text,
          })}
        >
          Task List Management
        </Typography>
        <IconButton
          size="small"
          onClick={() => setHelpOpen(true)}
          aria-label="Help"
        >
          <HelpOutlineIcon
            sx={(theme) => ({ color: theme.custom?.textLight })}
          />
        </IconButton>
      </Box>
      <Typography
        variant="body1"
        sx={(theme) => ({ mb: 3, color: theme.custom?.textLight })}
      >
        Manage your organization&apos;s inspection templates, damage types,
        property features, and zones.
      </Typography>

      {/* ── Paiv Templates Section ── */}
      {systemTemplates.length > 0 && (
        <Box mb={4}>
          <Typography
            variant="h6"
            sx={(theme) => ({
              mb: 1.5,
              fontWeight: 600,
              color: theme.custom?.text,
            })}
          >
            FieldInspect Templates
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {systemTemplates.map((t) => {
              const itemCount = (t.items ?? []).filter(
                (i) => !i.isDeleted
              ).length;
              const displayStatus = getDisplayStatus(t.status);
              return (
                <Paper
                  key={t.id}
                  variant="outlined"
                  onClick={() => setViewingTemplateId(t.id)}
                  sx={(theme) => ({
                    display: 'flex',
                    alignItems: 'center',
                    px: 2.5,
                    py: 2,
                    cursor: 'pointer',
                    borderRadius: '10px',
                    borderColor: theme.custom?.stroke,
                    backgroundColor: theme.custom?.background1,
                    '&:hover': {
                      borderColor: theme.custom?.primary,
                      backgroundColor: theme.custom?.background2,
                    },
                  })}
                >
                  <LockIcon
                    sx={(theme) => ({
                      fontSize: 18,
                      color: theme.custom?.textLight,
                      mr: 1.5,
                    })}
                  />
                  <Box flex={1}>
                    <Typography
                      sx={(theme) => ({
                        fontSize: 15,
                        fontWeight: 600,
                        color: theme.custom?.text,
                      })}
                    >
                      {t.name}
                    </Typography>
                    <Typography
                      sx={(theme) => ({
                        fontSize: 13,
                        color: theme.custom?.textLight,
                        mt: 0.25,
                      })}
                    >
                      {itemCount} tasks · v{t.version}
                      {t.description ? ` · ${t.description}` : ''}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={displayStatus}
                      color={getStatusColor(t.status)}
                      size="small"
                    />
                    {!isPaivcamSeed && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloneSystemTemplate(t.id);
                        }}
                        disabled={cloning}
                        sx={{ textTransform: 'none' }}
                      >
                        Clone
                      </Button>
                    )}
                    <ChevronRightIcon
                      sx={(theme) => ({
                        color: theme.custom?.textLight,
                      })}
                    />
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Box>
      )}

      {/* ── Your Templates Section ── */}
      <Box>
        <Typography
          variant="h6"
          sx={(theme) => ({
            mb: 1.5,
            fontWeight: 600,
            color: theme.custom?.text,
          })}
        >
          Your Templates
        </Typography>
        {orgTemplates.length === 0 ? (
          <Typography sx={(theme) => ({ color: theme.custom?.textLight })}>
            No custom templates yet. Clone a FieldInspect template above to get
            started.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {orgTemplates.map((t) => {
              const itemCount = (t.items ?? []).filter(
                (i) => !i.isDeleted
              ).length;
              const displayStatus = getDisplayStatus(t.status);
              return (
                <Paper
                  key={t.id}
                  variant="outlined"
                  onClick={() => setViewingTemplateId(t.id)}
                  sx={(theme) => ({
                    display: 'flex',
                    alignItems: 'center',
                    px: 2.5,
                    py: 2,
                    cursor: 'pointer',
                    borderRadius: '10px',
                    borderColor: theme.custom?.stroke,
                    backgroundColor: theme.custom?.background1,
                    '&:hover': {
                      borderColor: theme.custom?.primary,
                      backgroundColor: theme.custom?.background2,
                    },
                  })}
                >
                  <Box flex={1}>
                    <Typography
                      sx={(theme) => ({
                        fontSize: 15,
                        fontWeight: 600,
                        color: theme.custom?.text,
                      })}
                    >
                      {t.name}
                    </Typography>
                    <Typography
                      sx={(theme) => ({
                        fontSize: 13,
                        color: theme.custom?.textLight,
                        mt: 0.25,
                      })}
                    >
                      {itemCount} tasks · v{t.version}
                      {t.description ? ` · ${t.description}` : ''}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={displayStatus}
                      color={getStatusColor(t.status)}
                      size="small"
                    />
                    <ChevronRightIcon
                      sx={(theme) => ({
                        color: theme.custom?.textLight,
                      })}
                    />
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Help Dialog */}
      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle>How Task List Management Works</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
            Templates
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.custom?.textLight })}
            paragraph
          >
            A template defines the full inspection workflow — the checklist
            tasks, damage types, property features, and zones that inspectors
            use. Each org can have one published template at a time. Clone a
            FieldInspect template to get started, then customize it for your
            team.
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Checklist
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.custom?.textLight })}
            paragraph
          >
            The checklist is the ordered list of tasks every inspector completes
            — property photos, material close-ups, condition notes, etc. Tasks
            are grouped by zone and completed in order during an inspection.
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Damage Types
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.custom?.textLight })}
            paragraph
          >
            Damage types are what Hank matches when an inspector verbally
            reports damage. Use clear, natural names like &quot;Hail
            Damage&quot; or &quot;Missing Shingles&quot; — Hank uses the name
            and description to match spoken words. Add a detailed description
            with keywords inspectors might say to improve matching accuracy.
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Property Features
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.custom?.textLight })}
            paragraph
          >
            Property features are structural elements or characteristics that
            may or may not be present on a property — like chimneys, skylights,
            or pipe jacks. When an inspector mentions a feature, Hank matches it
            by name and description. Each feature can have required
            documentation tasks (photos, questions).
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Zones
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.custom?.textLight })}
            paragraph
          >
            Zones organize tasks by location — like &quot;Ground Level&quot; and
            &quot;Roof Level&quot;. Inspectors work through tasks zone by zone.
            You can create custom zones to match your inspection workflow.
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Naming Tips for Hank
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.custom?.textLight })}
          >
            Hank uses trigram similarity to match what inspectors say to your
            damage types and property features. For best results: use plain
            English names (not codes), add descriptions with common synonyms and
            keywords, and test by saying the name out loud — if it sounds
            natural, Hank will match it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Got it</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TaskListManagement;
